import React, { useState, useMemo, useEffect, useRef } from "react";
import { MDBIcon, MDBLoadingManagement, MDBSwitch, MDBTooltip } from "mdb-react-ui-kit";
import CommonViewLayout from "../CommonViewLayout";
import ExportDropdown from "../../CommonViewComponents/ExportDropdown";
import usePermissionForPages from "../../../../Hooks/usePermissionForPages";
import { useTranslation } from "../../../../Context/TranslationContext";
import DataTable from "../../../../Components/Datatable/DataTable";
import MerlinModal from "../../Merlin/MerlinModal";
import useAxiosCall from "../../../../Hooks/useAxiosCall";
import { useLoadingOverlay } from "../../../../Context/LoadingOverlayContext";
import { decryptData, encryptData } from "../../../../Utilities/CryptoUtility";
import AddUser from "./AddUser";
import UserAction from "../../CommonViewComponents/UserAction";
import { SecureStorage } from "../../../../Utilities/StorageEncryption"
import EditUser from "./EditUser";
import { SuccessAlert, ErrorAlert } from "../../../../Components/Alert";

const User = () => {
  const [data, setData] = useState({ columns: [], rows: [] });
  const basicRef = useRef(null);
  const [isLoaderOpen, setIsLoaderOpen] = useState(false);
  const { translations } = useTranslation();
  const { isVisible, showLoadingOverlay, hideLoadingOverlay } = useLoadingOverlay();
  let isMainLoaderVisible = isVisible;
  usePermissionForPages(['VendorPortal'], '/administrator/users');
  const [tableState, setTableState] = useState({
    pageNumber: 1,
    pageSize: 10,//move to env
    sortColumn: '',
    sortDirection: '',
    searchValues:[],
  });

  const [isChecked, setIsChecked] = useState(true);
  const [toggle, setToggle] = useState(true);

  const handleToggleChange = () => {
    setToggle(prevValue => !prevValue);
    const updateTableState = (prevTableState, isCurrentlyChecked) => {
      if (!isCurrentlyChecked) {
        return { ...prevTableState, pageNumber: 1 };
      }
      const updatedSearchValues = prevTableState.searchValues.filter(obj => 
        !(obj.filterColumn === "inactive" && obj.filterValue == "0")
      );
  
      return {
        ...prevTableState,
        searchValues: updatedSearchValues,
        pageNumber: 1
      };
    };
    setIsChecked(prevChecked => {
      setTableState(prevTableState => updateTableState(prevTableState, prevChecked));
      return !prevChecked;
    });
  };


  useEffect(() => {
    getUserPortalData();
  }, [tableState, isChecked]);

  const handleApiCallSuccess = (encryptedData) => {
    let data = decryptData(encryptedData.data);
    data = data.residents;
    const transformedColumns = [
      {
        caption: "Actions",
        defaultValue: null,
        fieldName: "actions",
        fieldType: null,
        filterType: null,
        filterValues: null,
        sortable: false,
        visible: "Yes"
      },
      ...data.columns
    ];

    
    const transformedRows = data.rows.map((detail, i) => {
      let actionParams = {
        'applicationId': detail.applicationId,
        'username': detail.username
      };
      return {
      ...detail,
      actions: <UserAction 
        index={i} 
        userName={detail.prospectName} 
        email={detail.portalEmail} 
        portal="user" 
        actionParams={actionParams}
        onEditUser={loadUserData}
        onRefreshData={getUserPortalData}
      />,
      }
    });
    setData({ columns: transformedColumns, rows: transformedRows, totalRecords: data.totalRecords });
    let columnsData = transformedColumns.map((item) => {
      return item.fieldName;
    })
    setSelectedColumns(columnsData)
    const visibleColumns = transformedColumns.filter(column => column.visible === "Yes");
    setMerlinColumns(visibleColumns)
  };

  const handleApiCallError = (errorMessage) => {
    console.log(errorMessage);
  };

  const { makeApiCall } = useAxiosCall();

  const getUserPortalData = async (hideLoader = false) => {
    let searchVal = tableState.searchValues;
    if (isChecked) {
      searchVal = searchVal.filter(obj => obj.filterColumn !== "inactive");
      
      let newObj = {
        filter: "Contains",
        filterColumn: "inactive",
        filterValue: isChecked ? "0" : "1"
      };
      searchVal.push(newObj);
    }
    const requestData = {
      data: encryptData(JSON.stringify(
        {
          page: tableState.pageNumber,
          pageSize: tableState.pageSize,
          sortColumn: tableState.sortColumn,
          sortDirection: tableState.sortDirection,
          searchValues: searchVal,
          domain: SecureStorage.getItem('domain'),
          company: SecureStorage.getItem('companyKey'),
        }
      ))
    };

    let isRequestCancelled = false;

    try {
      // if (!hideLoader) {
        setIsLoaderOpen(true);
      // }
      await makeApiCall({
        requestData,
        onSuccess: handleApiCallSuccess,
        onError: handleApiCallError,
        requestType: 'POST',
        url: 'getDataListing/user',
        loader: false
      });
    } catch (error) {
      if (error.message == 'Request canceled') {
        isRequestCancelled = true;
      } else {
        console.error('API call error:', error);
      }
    } finally {
      if (!isRequestCancelled) {
        setIsLoaderOpen(false)
      }
    }
  };

  const [selectedColumns, setSelectedColumns] = useState([]);
  const [merlinColumns, setMerlinColumns] = useState([]);

  const [isMerlinModalOpen, setIsMerlinModalOpen] = useState(false);

  const openMerlinModal = () => {
    setIsMerlinModalOpen(true);
  };

  const closeMerlinModal = () => {
    setIsMerlinModalOpen(false);
  };
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);

  const openAddUserModal = () => {
    setIsAddUserModalOpen(true);
  };

  const closeAddUserModal = (userAdded = false) => {
    setIsAddUserModalOpen(false);
    if (userAdded) {
      let hideLoader = true;
      getUserPortalData(hideLoader);
    }
  };
  
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [currentEditUserData, setCurrentEditUserData] = useState(null);

  const loadUserData = async (username) => {
    const requestData = {
      data: encryptData(JSON.stringify({
        companyPrefix: SecureStorage.getItem('companyKey'),
        username: username
      }))
    };

    const domainRequestData = {
      data: encryptData(JSON.stringify({
        username: username
      }))
    };

    // Use global loader - same as EditUser
    showLoadingOverlay();

    try {
      // First API call
      const userData = await new Promise((resolve, reject) => {
        makeApiCall({
          requestData,
          onSuccess: (data) => resolve(data.data),
          onError: (errorMessage) => reject(new Error(errorMessage)),
          requestType: 'POST',
          url: 'loadUser',
          loader: false  // No automatic loader
        });
      });

      // Re-show loader immediately after first API call
      showLoadingOverlay();

      // Second API call
      const domainData = await new Promise((resolve, reject) => {
        makeApiCall({
          requestData: domainRequestData,
          onSuccess: (data) => resolve(data.data),
          onError: (errorMessage) => reject(new Error(errorMessage)),
          requestType: 'POST',
          url: 'getUserDomainAccess',
          loader: false  // No automatic loader
        });
      });

      // Both succeeded, combine data and open modal
      const combinedData = {
        ...userData,
        domains: domainData.domains
      };

      setCurrentEditUserData(combinedData);
      setIsEditUserModalOpen(true);

    } catch (error) {
      console.error('Error loading data:', error);
      handleEditUserError('Failed to load user data.');
    } finally {
      // Use global loader - same as EditUser
      hideLoadingOverlay();
    }
  };

  const [alert, setAlert] = useState({ type: '', message: '' });
  const [alertTimeout, setAlertTimeout] = useState(null);

  useEffect(() => {
    return () => {
      if (alertTimeout) {
        clearTimeout(alertTimeout);
      }
    };
  }, [alertTimeout]);

  const closeEditUserModal = (userUpdated = false   ) => {
    setIsEditUserModalOpen(false);
    setCurrentEditUserData(null);
    if (userUpdated) {
      let hideLoader = true;
      getUserPortalData(hideLoader);
    }
  };

  const handleAddUserSuccess = (message) => {
    if (alertTimeout) {
      clearTimeout(alertTimeout);
    }

    setAlert({ type: 'success', message: message });
    
    const timeout = setTimeout(() => {
      setAlert({ type: '', message: '' });
    }, 5000);
    setAlertTimeout(timeout);
  };

  const handleAddUserError = (errorMessage) => {
    if (alertTimeout) {
      clearTimeout(alertTimeout);
    }

    setAlert({ type: 'error', message: errorMessage });
    
    const timeout = setTimeout(() => {
      setAlert({ type: '', message: '' });
    }, 5000);
    setAlertTimeout(timeout);
  };

  const handleEditUserSuccess = (message) => {
    if (alertTimeout) {
      clearTimeout(alertTimeout);
    }

    setAlert({ type: 'success', message: message });
    
    const timeout = setTimeout(() => {
      setAlert({ type: '', message: '' });
    }, 5000);
    setAlertTimeout(timeout);
  };

  const handleEditUserError = (errorMessage) => {
    if (alertTimeout) {
      clearTimeout(alertTimeout);
    }

    setAlert({ type: 'error', message: errorMessage });
    
    const timeout = setTimeout(() => {
      setAlert({ type: '', message: '' });
    }, 5000);
    setAlertTimeout(timeout);
  };

  const UserFilters = useMemo(() => (
    <div className="d-flex align-items-center" >
      <div style={{ minWidth: 'max-content', cursor: 'pointer', marginRight:'18px' }} onClick={openAddUserModal}>
        <MDBTooltip tag='span' wrapperProps={{ color: 'secondary' }} title="Add user">
          <MDBIcon fas icon="add" style={{  }} />
        </MDBTooltip>
      </div>
      <div style={{ minWidth: 'max-content' }}>
        <ExportDropdown noMargin portalType={'user'} filters={tableState} label="User" />
      </div>
      <div style={{ minWidth: 'max-content', cursor: 'pointer' }} onClick={openMerlinModal}>
        <MDBTooltip tag='span' wrapperProps={{ color: 'secondary' }} title="Settings">
          <MDBIcon fas icon="cogs" style={{  }} />
        </MDBTooltip>
      </div>
      <div style={{ minWidth: 'max-content', cursor: 'pointer', marginLeft:18 }}>
        <div className="switch-container">
          <div className="left-label">All</div>
          <MDBSwitch className="custom-switch" checked={isChecked} onChange={handleToggleChange} />
          <div className="right-label">Active</div>
        </div>
      </div>
    </div>
  ), [tableState, isChecked]);

  const handleSort = (column, direction) => {
    setTableState((prev) => ({
      ...prev,
      pageNumber: 1,
      sortColumn: column,
      sortDirection: direction,
    }));
  };

  const handleSearch = (searchValues) => {
    setTableState((prev) => ({
      ...prev,
      pageNumber: 1,
      searchValues,
    }));
  };


  const handlePageChange = (pageNumber) => {
    setTableState((prev) => ({
      ...prev,
      pageNumber,
    }));
  };
  const handleRowsPerPageChange = (size) => {
    setTableState((prev) => ({
      ...prev,
      pageSize : size,
    }));
  };

  return (
    <CommonViewLayout
      headerTitle="User Management"
      filters={UserFilters}
      iconName="user"
      isReport={true}
    >
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 9999,
        width: '350px',
        maxWidth: '90vw',
        pointerEvents: (alert.type === 'success' || alert.type === 'error') ? 'auto' : 'none'
      }}>
        {alert.type === 'success' && (
          <SuccessAlert 
            success={true} 
            messages={[alert.message]} 
            resetAlert={setAlert} 
          />
        )}
        {alert.type === 'error' && (
          <ErrorAlert 
            error={true} 
            messages={[alert.message]} 
            resetAlert={setAlert} 
          />
        )}
      </div>

      <DataTable
        data={data}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onSort={handleSort}
        onSearch={handleSearch}
        portal={'user'}
        search={true}
        toggleButton={toggle}
      />
      {isMerlinModalOpen && (
        <MerlinModal
          isOpen={isMerlinModalOpen}
          onClose={closeMerlinModal}
          columns={merlinColumns}
          selectedColumns={selectedColumns}
          setSelectedColumns={setSelectedColumns}
          portal={'user'}
        />
      )}
      {isAddUserModalOpen && (
        <AddUser
          isOpen={isAddUserModalOpen}
          onClose={closeAddUserModal}
          onUpdateSuccess={handleAddUserSuccess}
          onUpdateError={handleAddUserError}
          editMode={true}
        />
      )}
      {isEditUserModalOpen && (
        <EditUser
          isOpen={isEditUserModalOpen}
          onClose={closeEditUserModal}
          onUpdateSuccess={handleEditUserSuccess}
          onUpdateError={handleEditUserError}
          editMode={true}
          userData={currentEditUserData}
        />
      )}
      
      {(isLoaderOpen && isMainLoaderVisible != true) &&
        <MDBLoadingManagement parentRef={basicRef} backdrop={false} loadingText="" backdropOpacity={0} />
      }
    </CommonViewLayout>
  );
};

export default User;
